import { useLiveQuery } from 'dexie-react-hooks';
import type { MealSlot, SavedMeal } from '@/engine/types';
import {
  db,
  newId,
  nowIso,
  putAndQueue,
  softDeleteAndQueue,
  type MealRow,
  type SavedMealRow,
} from '@/lib/db';

/**
 * Comidas guardadas (SPEC 6.B).
 *
 * Es el atajo de lo que se repite tal cual: el mismo desayuno cinco días a la
 * semana son cinco alimentos y quince toques cada mañana. Se guarda una COPIA
 * de los nutrientes, como en el registro (SPEC 4.2): la comida guardada no
 * cambia porque alguien corrija después la ficha del alimento.
 */
export function useSavedMeals(): SavedMealRow[] {
  const rows = useLiveQuery(async () => db.savedMeals.toArray(), [], []);
  return (rows ?? [])
    .filter((r) => r.deletedAt === null)
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

/** Guarda los registros de una franja del día como comida reutilizable. */
export async function saveMealFromEntries(
  name: string,
  entries: readonly MealRow[],
): Promise<void> {
  const items = entries.map(({ foodId, foodName, grams, per100g, sources }) => ({
    foodId,
    foodName,
    grams,
    per100g: { ...per100g },
    sources: { ...sources },
  }));
  if (items.length === 0) return;

  const meal: SavedMeal = { id: newId(), name: name.trim(), items };
  await putAndQueue('savedMeals', meal.id, { ...meal, updatedAt: nowIso(), deletedAt: null });
}

/** Registra de una vez todos los alimentos de una comida guardada. */
export async function addSavedMeal(meal: SavedMeal, date: string, slot: MealSlot): Promise<number> {
  for (const item of meal.items) {
    const id = newId();
    await putAndQueue('meals', id, {
      ...item,
      id,
      date,
      slot,
      updatedAt: nowIso(),
      deletedAt: null,
    });
  }
  return meal.items.length;
}

export async function deleteSavedMeal(id: string): Promise<void> {
  await softDeleteAndQueue('savedMeals', id);
}
